import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type RecommendationSettings = {
  homeEnabled: boolean;
  cartEnabled: boolean;
  productsPerShelf: number;
  maxCategoryShelves: number;
};

export default function WorkspaceRecommendationSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const [model, setModel] = React.useState<RecommendationSettings>({
    homeEnabled: true,
    cartEnabled: true,
    productsPerShelf: 8,
    maxCategoryShelves: 3,
  });
  const [busy, setBusy] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/recommendations`);
      if (!res.ok) throw new Error('Failed to load recommendation settings');
      const data = (await res.json()) as RecommendationSettings;
      setModel(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load recommendation settings');
    } finally {
      setBusy(false);
    }
  }, [globalAuthorizedFetch]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(model),
      });
      if (!res.ok) throw new Error('Failed to save recommendation settings');
      const data = (await res.json()) as RecommendationSettings;
      setModel(data);
      setSuccess('Recommendation settings updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save recommendation settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">Recommendation strategy</h1>
          <p className="workspace-page-subtitle text-slate-600">
            Configure homepage shelves and cart recommendation aggressiveness.
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {busy ? <p className="text-sm text-slate-600">Loading...</p> : null}
          {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
          {success ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p> : null}

          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <label className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900">Homepage recommendations</span>
                <input type="checkbox" checked={model.homeEnabled} onChange={(e) => setModel((prev) => ({ ...prev, homeEnabled: e.target.checked }))} />
              </div>
              <p className="mt-1 text-xs text-slate-600">Enable merchandising rails on the storefront homepage.</p>
            </label>

            <label className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900">Cart recommendations</span>
                <input type="checkbox" checked={model.cartEnabled} onChange={(e) => setModel((prev) => ({ ...prev, cartEnabled: e.target.checked }))} />
              </div>
              <p className="mt-1 text-xs text-slate-600">Show related products and frequently bought together in cart.</p>
            </label>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-semibold text-slate-700">
              Products per shelf
              <input
                type="number"
                min={4}
                max={16}
                value={model.productsPerShelf}
                onChange={(e) => setModel((prev) => ({ ...prev, productsPerShelf: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>

            <label className="text-sm font-semibold text-slate-700">
              Category shelves on homepage
              <input
                type="number"
                min={1}
                max={6}
                value={model.maxCategoryShelves}
                onChange={(e) => setModel((prev) => ({ ...prev, maxCategoryShelves: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <div className="mt-5">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

